import { v3 } from "oc-threejs/short3";
import * as Maths from "oc-commons/Maths";
import { CameraPathHelper } from "oc-threejs/cameraautomatic/CameraPathHelper";
import { CameraTuple } from "oc-threejs/cameraautomatic/CameraTuple";
import { easeInOut } from "oc-threejs/cameraautomatic/CameraPath";
import React from "react";
import { DebouncedFunc } from "lodash";
import { Vector3 } from "three";
import { getWorldCenterOfSegmentParts, getWorldPositionsOfEdges } from "./MHelper";
import { CameraReset } from "../components/zauneditor/CameraReset";

/**
 * Funktionen um die Kamera umzustellen und die PreviewBilder zu rendern
 */
export class PreviewPictures {
  private isReady: boolean; // Ist die Kamera initialisiert?
  private debouncedRender: DebouncedFunc<() => void>;
  orthographicMovement = new OrthographicControls();

  /**
   * Ändert die Zaunplaner 3D-Szene um näher an die Darstellung des Torkonfigurators zu kommen
   * Entfernt Sky, Grundriss und Empties
   * Setzt Orthographische Kamera ein
   */
  init() {
    APP.on("menuSelect", (menuIndex: number, lastMenuIndex: number, pageKey: string) => {
      this.setOrthographicMode(pageKey != "Abschluss");
      this.debouncedRender();
    });

    this.setOrthographicMode(true);

    this.isReady = true;
    setTimeout(this.render, 100);
    this.debouncedRender = _.debounce(() => this.render(), 15, { leading: true });
    // Dirty Stage checker:
    setInterval(() => STAGE.isDirty && this.debouncedRender(), 200);
  }

  /**
   * Schaltet den Kamera-Modus um (orthographisch oder perspektivisch
   * @param orthographic Soll orthographisch sein?
   */
  setOrthographicMode(orthographic: boolean) {
    if (STAGE.orthographicMode == orthographic) return;

    STAGE.setOrthographicMode(orthographic, this.getConfig(true));

    if (orthographic) {
      document.body.classList.remove("bigPreview");
      setTimeout(() => STAGE.stopRenderLoop(), 10);
    } else {
      document.body.classList.add("bigPreview");
      this.zoomCameraToPartMassPerspective();
      STAGE.startRenderLoop();
    }
    APP.dirty();
  }

  /**
   * Generiert eine Kamerakonfiguration für STAGE.setOrthographicCameraConfig
   * @param front Soll die Kamera auf die Front des Objekts schauen?
   */
  getConfig(front: boolean) {
    const objectToLookAt =
      APP.arrangement.getCurrentElement()?.jaco3d ||
      APP.arrangement.getSegment()?.jaco3d ||
      APP.arrangement.jaco3d;
    return {
      object: objectToLookAt,
      zoomDirection: v3(0, 0, front ? -0.001 : 0.001),
      up: v3(0, 1, 0),
      border: 0.2,
    };
  }

  /**
   * Setzt die Ansicht wieder mittig auf das Zaunelement
   */
  resetView() {
    this.orthographicMovement.resetView();
    this.zoomCameraToPartMassPerspective();
  }

  /**
   * Triggert das Rendern von Vorder- und Rückansicht
   */
  render() {
    if (!this.isReady) return;

    this.hideOrShowObjectsDependingOnCameraMode(STAGE.orthographicMode);

    this.renderSides();
  }

  /**
   * Rendert eine Seite und setzt die Bilddaten in das entsprechende Bild oder rendert die Scene
   */
  renderSides() {
    const segment = APP.arrangement.getSegment();
    if (segment?.jaco3d == null) return;

    const parts = APP.arrangement.getPartsAndPfosten();
    parts.forEach((p) => {
      const hoverBox = p.jaco3d?.hoverBox;
      if (hoverBox) {
        hoverBox.visible = false;
      }
    });

    // Zeichne Rückseite (exklusive Bemaßung) zuerst:
    APP.arrangement.massRuler.setSideToFront(false);
    if (STAGE.orthographicMode) {
      STAGE.setOrthographicCameraConfig(this.getConfig(false));
      this.centerAndTranslateOrthographicCamera();
    }

    const imageElement = document.getElementById("previewPictureBack") as HTMLImageElement | null;
    if (imageElement != null) imageElement.src = STAGE.renderToCanvas().toDataURL("image/png");

    // Zeichne Vorderansicht (inklusive Bemaßung):
    APP.arrangement.massRuler.setSideToFront(true);
    if (STAGE.orthographicMode) {
      STAGE.setOrthographicCameraConfig(this.getConfig(true));
      this.centerAndTranslateOrthographicCamera();
    }
    STAGE.renderSceneOnce();
  }

  /**
   * Zentriert die orthographische Kamera auf die Teile des Segments und führt danach die User-Translation aus
   * @private
   */
  private centerAndTranslateOrthographicCamera() {
    const centerX = getWorldCenterOfSegmentParts(APP.arrangement.getSegment());
    STAGE.orthographicCamera.position.x = centerX;
    STAGE.orthographicCamera.updateProjectionMatrix();
    const delta = this.orthographicMovement.getDeltaWorld();
    STAGE.orthographicCamera.position.x -= delta.x;
    STAGE.orthographicCamera.position.y += delta.y;
  }

  /**
   * Kopie von APP.zoomCameraToPart, aber mit eigener lookDirection, zoom und height
   * @param part Teil auf das gezoomt wird
   * @param linear
   * @param lookDirection
   * @param zoom
   * @param height Augenposition, in Abhängigkeit der Elementhöhe
   * @returns {*}
   */
  zoomCameraToPartMassPerspective(
    part = APP.arrangement.getCurrentElement(),
    linear = true,
    lookDirection = v3(0, 0.25, 1),
    zoom = 1,
    height = 0.5
  ) {
    const targetJaco = part ?? APP.arrangement;

    const box = targetJaco.getBox();
    box.applyMatrix4(targetJaco.matrixWorld);
    box.max.y = Math.max(3, box.max.y) * height;

    const pathHelper = new CameraPathHelper();
    const [target, position] = STAGE.getCameraConfigAsZoomToBox(box, lookDirection, zoom);
    const path = linear
      ? pathHelper.linearPathTo(new CameraTuple(position, target), easeInOut)
      : pathHelper.dronePathTo(new CameraTuple(position, target), 5, easeInOut);

    const pathDistance = path.getAt(0).position.distanceTo(path.getAt(1).position);
    const duration =
      (linear || path.isLinear ? 0 : 3500) * Maths.mapToUnitInterval(0, pathDistance, 30) + 600;

    return pathHelper.run(path, duration).then(() => {
      APP.trigger("controlsEnd");
    });
  }

  private hideOrShowObjectsDependingOnCameraMode(orthographic: boolean) {
    const sky = APP.scene.getObjectByName("sky");
    if (sky) sky.visible = !orthographic;
    const grundrissShape = APP.scene.getObjectByName("grundrissShape");
    if (grundrissShape) grundrissShape.visible = !orthographic;
    const empty = APP.scene.getObjectByName("Empty");
    if (empty) empty.visible = !orthographic;
  }
}

/**
 * Hilfsklasse für die 2D-Bewegungen
 */
class OrthographicControls {
  private isCameraMoving = false;
  private downScreenPos = v3(); // Hier war die Maus bei Klick (Start der Translationsbewegung)
  private currentScreenPos = v3(); // Hier ist die Maus mit Klick zuletzt gewesen
  private lastDeltaScreen = v3(); // Nach dem Absetzen ist hier die letzte Verschiebung gespeichert (um die nächste Aktion direkt so zu verschieben)

  /**
   * Zoome rein oder raus, je nach Mausradbewegung, dann rendere neu.
   * @param e MausEvent. Überprüfe deltaY hier.
   */
  zoom(e: React.WheelEvent) {
    if (!STAGE.orthographicMode) return;

    if (e.deltaY > 0.1) {
      STAGE.orthographicCamera.zoom *= 0.9;
    } else if (e.deltaY < -0.1) {
      STAGE.orthographicCamera.zoom *= 1.1;
    }
    STAGE.orthographicCamera.zoom = Maths.cutBetween(0.6, STAGE.orthographicCamera.zoom, 5);
    APP.previewPictures.render();
  }

  /**
   * Gibt dem Renderer direkt an wie weit die aktuelle Position von der Standardansicht weg ist
   * So weit wird dann die Kamera verschoben
   */
  getDeltaWorld() {
    const downWorldPos = this.screenToWorld(this.downScreenPos);
    const currentWorldPos = this.screenToWorld(this.currentScreenPos);
    return downWorldPos.sub(currentWorldPos);
  }

  /**
   * Starte das Tracking der Maus und berücksichtige dabei die Verschiebung der letzten Aktion
   * @param e React.MouseEvent
   */
  startMoving(e: React.MouseEvent) {
    if (!STAGE.orthographicMode) {
      this.isCameraMoving = false;
      return;
    }
    this.isCameraMoving = true;
    this.downScreenPos = v3(e.nativeEvent.layerX, e.nativeEvent.layerY, 0).add(
      this.lastDeltaScreen
    );
  }

  /**
   * Aktualisiert die aktuelle Mausposition und rendert neu
   * @param e React.MouseEvent
   */
  updateMoving(e: React.MouseEvent) {
    if (!this.isCameraMoving) return;

    this.currentScreenPos = v3(e.nativeEvent.layerX, e.nativeEvent.layerY, 0);
    APP.previewPictures.render();
    APP.ui.dirty();
  }

  /**
   * Aktualisiere die letzte Mausbewegung nicht mehr und speichere die letzte Verschiebung (Screen)
   */
  stopMoving() {
    this.isCameraMoving = false;
    this.lastDeltaScreen = this.downScreenPos.clone().sub(this.currentScreenPos);
  }

  hasMoved() {
    return Math.abs(this.downScreenPos.x) + Math.abs(this.downScreenPos.y) > 0;
  }

  /**
   * Setze Zoom und Translationen zurück
   */
  resetView() {
    STAGE.orthographicCamera.zoom = 1.2;
    this.downScreenPos = v3();
    this.currentScreenPos = v3();
    this.lastDeltaScreen = v3();
  }

  /**
   * Hilfsfunktion, die die gegebenen Screen-Koordinaten in Welt-Koordinaten umrechnet
   * @param pos Vector3
   */
  screenToWorld(pos: Vector3): Vector3 {
    const stageRect = document.querySelector(".stage").getBoundingClientRect();
    if (stageRect.width < 10) stageRect.width = 10;
    if (stageRect.height < 10) stageRect.height = 10;

    const worldPos = v3(
      (pos.x * 2) / stageRect.width - 1,
      (-pos.y * 2) / stageRect.height + 1,
      0
    ).unproject(STAGE.orthographicCamera);
    return worldPos;
  }

  worldToScreen(position: Vector3): Vector3 {
    const screenPosition = position.clone().project(STAGE.camera);
    const x = (screenPosition.x + 1) / 2;
    const y = -(screenPosition.y - 1) / 2;
    return v3(x, y, 0);
  }

  /**
   * Prüft ob wenigstens ein Stück der Parts in der Szene sichtbar ist.
   */
  partsVisible(): boolean {
    const coordinates = getWorldPositionsOfEdges(APP.arrangement.getSegment());
    const sc = coordinates.map((c) => this.worldToScreen(c as any));
    const vis = sc[0].x < 1 && sc[2].x > 0 && sc[0].y < 1 && sc[1].y > 0;
    return vis;
  }
}

/**
 * Vorschaubild mit Label drunter
 * @param name
 * @param label
 * @param children
 * @constructor
 */
export function PreviewPicture({
  name,
  label,
  children,
}: {
  name: string;
  label: string;
  children?: React.ReactNode;
}) {
  const orthographicMovement = APP.previewPictures?.orthographicMovement;

  const transparentPixel =
    "data:img/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

  return (
    <figure
      className={"previewPicture " + name}
      onWheel={(e) => orthographicMovement.zoom(e)}
      onPointerDown={(e) => orthographicMovement.startMoving(e)}
      onPointerMove={(e) => orthographicMovement.updateMoving(e)}
      onPointerUp={(e) => orthographicMovement.stopMoving()}
      onPointerOut={(e) => orthographicMovement.stopMoving()}
      draggable={false}
      role="img"
      aria-label={I18N("3D-Vorschau") + ": " + label}
      tabIndex={0}
    >
      {children || (
        <img id={"previewPicture" + name} alt={label} src={transparentPixel} draggable={false} />
      )}
      <figcaption className="caption">{label}</figcaption>
      <CameraReset
        resetFunction={() => {
          APP.previewPictures?.resetView();
          APP.dirty();
        }}
        className={
          !(window as any).STAGE?.orthographicMode ||
          APP.previewPictures?.orthographicMovement.hasMoved()
            ? ""
            : "hidden"
        }
      />
    </figure>
  );
}
